import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import {
  Fingerprint, Search, Loader2, Download, Plus, X, Save,
  Wifi, WifiOff, RefreshCw, Trash2, Clock, BookOpen, Copy as CopyIcon, ChevronDown, ChevronUp, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BiometricLogs = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [devices, setDevices] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('all');
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deviceForm, setDeviceForm] = useState({
    device_name: '', device_type: 'biometric', device_serial: '',
    location_name: '', ip_address: '',
  });
  const [showGuide, setShowGuide] = useState(false);
  const [showApiKeyFor, setShowApiKeyFor] = useState({}); // per-device toggle

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/attendance/devices?company_id=${selectedCompany}`, { withCredentials: true });
      setDevices(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedDevice !== 'all') params.append('device_id', selectedDevice);
      if (search) params.append('employee_code', search);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const { data } = await axios.get(`${API_URL}/attendance/device/logs?${params}`, { withCredentials: true });
      setLogs(data);
    } catch (e) { console.error(e); setLogs([]); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, search, selectedDevice]);

  useEffect(() => { if (selectedCompany) { fetchDevices(); fetchLogs(); } }, [selectedCompany, fetchDevices, fetchLogs]);




  const handleAddDevice = async () => {
    if (!deviceForm.device_name || !deviceForm.device_serial) {
      toast.error('Device name and serial are required'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/attendance/devices`, {
        ...deviceForm, company_id: selectedCompany,
      }, { withCredentials: true });
      toast.success('Device registered');
      setShowAddDevice(false);
      setDeviceForm({ device_name: '', device_type: 'biometric', device_serial: '', location_name: '', ip_address: '' });
      fetchDevices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDeleteDevice = async (id) => {
    if (!window.confirm('Delete this device?')) return;
    try {
      await axios.delete(`${API_URL}/attendance/devices/${id}`, { withCredentials: true });
      toast.success('Device deleted');
      fetchDevices();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="biometric-logs-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Fingerprint className="inline-block w-8 h-8 mr-2 mb-1" /> Biometric & Device Logs
            </h1>
            <p className="mt-1 text-sm text-slate-600">Manage attendance devices and view raw punch logs</p>
          </div>
          <div className="flex gap-3">
            <a
              href="/attendance/biometric/mapping"
              data-testid="link-to-mapping"
              className="inline-flex items-center px-3 h-10 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
            >
              Device Mapping
            </a>
            <a
              href="/attendance/biometric/import"
              data-testid="link-to-etl"
              className="inline-flex items-center px-3 h-10 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
            >
              eTimeTrackLite Import
            </a>
            <Button variant="outline" data-testid="biometric-export-btn" onClick={() => {
              if (logs.length === 0) { toast.error('No logs to export'); return; }
              downloadCSV(logs.map(l => ({
                Employee: l.employee_code, Time: l.punch_time, Type: l.punch_type,
                Verification: l.verification_type, Device: l.device_name,
              })), 'device_punch_logs.csv');
              toast.success('Logs exported');
            }}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button data-testid="add-device-btn" onClick={() => setShowAddDevice(true)} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              <Plus className="w-4 h-4 mr-2" /> Register Device
            </Button>
          </div>
        </div>

        {/* Integration Guide (collapsible) */}
        <div className="bg-white border border-slate-200 rounded-xl" data-testid="biometric-integration-guide">
          <button
            onClick={() => setShowGuide(v => !v)}
            data-testid="biometric-guide-toggle"
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 rounded-xl"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-900">Integration Guide — connect your biometric / face device</p>
                <p className="text-xs text-slate-500">Webhook URL, sample payload, step-by-step for ZKTeco, ESSL, Matrix & CAMS devices.</p>
              </div>
            </div>
            {showGuide ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          {showGuide && (() => {
            const webhook = `${process.env.REACT_APP_BACKEND_URL}/api/attendance/device/push`;
            const sample = JSON.stringify({
              device_serial: 'ZK-T4-001',
              api_key: 'dk_XXXX-replace-with-key-shown-on-your-device-card',
              punches: [
                { employee_code: 'EMP001', punch_time: '2026-04-23T09:15:00', punch_type: 'in', verification_type: 'fingerprint' },
                { employee_code: 'EMP002', punch_time: '2026-04-23T09:16:12', punch_type: 'in', verification_type: 'face' },
              ],
            }, null, 2);
            const curl = `curl -X POST "${webhook}" \\
  -H "Content-Type: application/json" \\
  -d '${sample.replace(/'/g, "'\\''")}'`;
            return (
              <div className="px-5 pb-5 space-y-4 border-t border-slate-100 pt-4" data-testid="biometric-guide-body">
                {/* Webhook card */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Webhook URL (point every device to this)</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 font-mono text-xs text-slate-900 bg-white border border-slate-200 rounded px-2 py-1.5 truncate">{webhook}</code>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(webhook); toast.success('Webhook URL copied'); }} data-testid="copy-webhook-btn">
                      <CopyIcon className="w-3.5 h-3.5 mr-1" /> Copy
                    </Button>
                  </div>
                </div>

                {/* Sample payload + curl */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-slate-900 rounded-lg p-3">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Sample JSON body</p>
                    <pre className="font-mono text-[11px] text-emerald-200 whitespace-pre-wrap leading-relaxed">{sample}</pre>
                  </div>
                  <div className="bg-slate-900 rounded-lg p-3 relative">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Test with cURL</p>
                    <pre className="font-mono text-[11px] text-sky-200 whitespace-pre-wrap leading-relaxed">{curl}</pre>
                    <button
                      onClick={() => { navigator.clipboard.writeText(curl); toast.success('cURL copied'); }}
                      data-testid="copy-curl-btn"
                      className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 text-white"
                      title="Copy cURL"
                    >
                      <CopyIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Step-by-step per device */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <p className="font-semibold text-slate-800 mb-1">ZKTeco (K40 / F18 / iClock)</p>
                    <ol className="list-decimal list-inside space-y-1 text-slate-600 leading-relaxed">
                      <li>Admin Menu → Comm → Cloud Server → set <span className="font-mono">Server URL</span> to the webhook above.</li>
                      <li>Set <span className="font-mono">HTTP Mode</span> and paste the device&apos;s <span className="font-mono">api_key</span> (shown on its card) into <span className="font-mono">Auth Token</span>.</li>
                      <li>Save + restart the device. Push should arrive within 1 min.</li>
                    </ol>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <p className="font-semibold text-slate-800 mb-1">ESSL / Matrix / CAMS</p>
                    <ol className="list-decimal list-inside space-y-1 text-slate-600 leading-relaxed">
                      <li>Device web admin → API / Webhook → enable &quot;Push Punch Logs&quot;.</li>
                      <li>Server URL = webhook above. Auth header: <span className="font-mono">X-API-Key: &lt;api_key&gt;</span>.</li>
                      <li>Map device fields: employee_code ↔ Card No / UID, punch_time ↔ Timestamp.</li>
                    </ol>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <p className="font-semibold text-slate-800 mb-1">Face devices (Hikvision / ZKTeco SpeedFace)</p>
                    <ol className="list-decimal list-inside space-y-1 text-slate-600 leading-relaxed">
                      <li>Set <span className="font-mono">verification_type</span> in push = <span className="font-mono">face</span>.</li>
                      <li>Each face-verified punch is automatically listed on <span className="font-mono">/attendance/face-logs</span>.</li>
                      <li>For browser self-service face punches, share the <span className="font-mono">/attendance/face-punch</span> link with employees.</li>
                    </ol>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <p className="font-semibold text-slate-800 mb-1">No device? Use manual or browser face punch</p>
                    <ol className="list-decimal list-inside space-y-1 text-slate-600 leading-relaxed">
                      <li>Use the in-app <span className="font-mono">Punch Logs</span> page for manual entry.</li>
                      <li>Or share <span className="font-mono">/attendance/face-punch</span> with employees — they punch from any browser with a webcam. Logs appear under Face Attendance.</li>
                    </ol>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Devices Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {devices.map(d => (
            <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm" data-testid={`device-card-${d.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {d.is_active ? <Wifi className="w-4 h-4 text-green-500" /> : <WifiOff className="w-4 h-4 text-red-500" />}
                  <span className="text-sm font-semibold text-slate-800">{d.device_name}</span>
                </div>
                <button data-testid={`delete-device-${d.id}`} onClick={() => handleDeleteDevice(d.id)} className="text-slate-400 hover:text-red-500">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-xs text-slate-500">Serial: {d.device_serial}</p>
              <p className="text-xs text-slate-500">Type: {d.device_type}</p>
              <p className="text-xs text-slate-500">Location: {d.location_name || 'N/A'}</p>
              <p className="text-xs text-slate-400 mt-1">Pushes: {d.total_pushes || 0}</p>
              {d.api_key && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">API Key</p>
                  <div className="flex items-center gap-1">
                    <code className="flex-1 font-mono text-[10px] bg-slate-50 border border-slate-200 rounded px-1.5 py-1 truncate" data-testid={`apikey-${d.id}`}>
                      {showApiKeyFor[d.id] ? d.api_key : '•'.repeat(Math.min(d.api_key.length, 24))}
                    </code>
                    <button
                      onClick={() => setShowApiKeyFor(s => ({ ...s, [d.id]: !s[d.id] }))}
                      data-testid={`toggle-apikey-${d.id}`}
                      className="p-1 text-slate-500 hover:text-[#0052FF]"
                      title={showApiKeyFor[d.id] ? 'Hide' : 'Show'}
                    >
                      {showApiKeyFor[d.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => { navigator.clipboard.writeText(d.api_key); toast.success('API key copied'); }}
                      data-testid={`copy-apikey-${d.id}`}
                      className="p-1 text-slate-500 hover:text-[#0052FF]"
                      title="Copy key"
                    >
                      <CopyIcon className="w-3 h-3" />
                    </button>
                  </div>
                  <a
                    href={`${API_URL}/attendance/biometric/gateway/download?device_id=${d.id}`}
                    data-testid={`download-gateway-${d.id}`}
                    className="mt-2 inline-flex items-center text-[11px] font-medium text-[#0052FF] hover:underline"
                  >
                    <Download className="w-3 h-3 mr-1" /> Download Gateway Agent (.zip)
                  </a>
                </div>
              )}
            </div>
          ))}
          {devices.length === 0 && (
            <div className="col-span-4 bg-slate-50 border border-dashed border-slate-300 rounded-lg p-8 text-center">
              <Fingerprint className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No devices registered. Click "Register Device" to add one.</p>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} data-testid="biometric-company-select"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={selectedDevice} onChange={e => setSelectedDevice(e.target.value)} data-testid="biometric-device-filter"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            <option value="all">All Devices</option>
            {devices.map(d => <option key={d.id} value={d.id}>{d.device_name}</option>)}
          </select>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input data-testid="biometric-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee code..." className="w-full h-10 pl-10 pr-4 bg-white border border-slate-300 rounded-md text-sm" />
          </div>
          <input type="date" data-testid="biometric-date-from" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm" />
          <input type="date" data-testid="biometric-date-to" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm" />
          <Button variant="outline" onClick={fetchLogs} data-testid="biometric-filter-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Filter
          </Button>
        </div>

        {/* Logs Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full" data-testid="device-logs-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Employee Code</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Punch Time</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Verification</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Device</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-slate-500">
                  <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No punch logs found</p>
                  <p className="text-xs mt-1">Device punch data will appear here when devices push logs</p>
                </td></tr>
              ) : logs.map((log, i) => (
                <tr key={log.id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`log-row-${i}`}>
                  <td className="py-3 px-4 text-sm font-mono font-medium text-slate-900">{log.employee_code}</td>
                  <td className="py-3 px-4 text-sm text-slate-700 font-mono">{log.punch_time}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.punch_type === 'in' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {log.punch_type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600 capitalize">{log.verification_type}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{log.device_name}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.processed ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                      {log.processed ? 'Processed' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add Device Modal */}
        {showAddDevice && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="add-device-modal">
            <div className="bg-white rounded-lg w-full max-w-md flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between p-5 border-b flex-shrink-0">
                <h2 className="text-lg font-semibold">Register Device</h2>
                <button onClick={() => setShowAddDevice(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Device Name *</label>
                  <input data-testid="device-name-input" value={deviceForm.device_name} onChange={e => setDeviceForm({...deviceForm, device_name: e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Main Entrance Reader" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Device Type</label>
                    <select data-testid="device-type-select" value={deviceForm.device_type} onChange={e => setDeviceForm({...deviceForm, device_type: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="biometric">Biometric (Fingerprint)</option>
                      <option value="face_recognition">Face Recognition</option>
                      <option value="card_reader">Card Reader</option>
                      <option value="mobile">Mobile App</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Serial Number *</label>
                    <input data-testid="device-serial-input" value={deviceForm.device_serial} onChange={e => setDeviceForm({...deviceForm, device_serial: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="SN-12345" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
                  <input data-testid="device-location-input" value={deviceForm.location_name} onChange={e => setDeviceForm({...deviceForm, location_name: e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Office Main Gate" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">IP Address</label>
                  <input data-testid="device-ip-input" value={deviceForm.ip_address} onChange={e => setDeviceForm({...deviceForm, ip_address: e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="192.168.1.100" />
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t flex-shrink-0">
                <Button variant="outline" onClick={() => setShowAddDevice(false)}>Cancel</Button>
                <Button data-testid="save-device-btn" onClick={handleAddDevice} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Register
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default BiometricLogs;
